import { normalizeTestName } from "./patientCognitiveRadar";

const ANXIETY_ALIASES = ["anxiety_beck_test", "beck_anxiety"];
const DEPRESSION_ALIASES = ["depression_beck_test", "beck_depression"];

export const BECK_ANXIETY_RANGES = [
  { min: 0, max: 21, label: "Muy baja", level: 0 },
  { min: 22, max: 35, label: "Moderada", level: 1 },
  { min: 36, max: 63, label: "Severa", level: 2 },
];

export const BECK_DEPRESSION_RANGES = [
  { min: 0, max: 13, label: "Mínima", level: 0 },
  { min: 14, max: 19, label: "Leve", level: 1 },
  { min: 20, max: 28, label: "Moderada", level: 2 },
  { min: 29, max: 63, label: "Grave", level: 3 },
];

export const BECK_THRESHOLD_LINES = [
  { value: 14, label: "Leve", test: "Depresión" },
  { value: 20, label: "Moderado", test: "Depresión" },
  { value: 29, label: "Severo", test: "Depresión" },
  { value: 22, label: "Moderado", test: "Ansiedad" },
  { value: 36, label: "Severo", test: "Ansiedad" },
];

const matchesTest = (testName, aliases) => {
  const normalized = normalizeTestName(testName);
  return aliases.some(
    (alias) => normalized === alias || normalized.includes(alias)
  );
};

const pickLatestTest = (tests) => {
  if (!tests.length) return null;
  return tests.reduce((latest, test) => {
    if (!latest) return test;
    return new Date(test.testDay) > new Date(latest.testDay) ? test : latest;
  }, null);
};

const getScore = (test) => {
  const score = test?.testResultNumber;
  if (score == null || Number.isNaN(Number(score))) return null;
  return Number(score);
};

export const getBeckSeverity = (ranges, score) => {
  if (score == null) return null;
  return (
    ranges.find((range) => score >= range.min && score <= range.max) || null
  );
};

export const getAffectiveProfile = (anxietyScore, depressionScore) => {
  if (anxietyScore == null && depressionScore == null) return null;

  const anxietySeverity = getBeckSeverity(BECK_ANXIETY_RANGES, anxietyScore);
  const depressionSeverity = getBeckSeverity(
    BECK_DEPRESSION_RANGES,
    depressionScore
  );

  const anxietyLevel = anxietySeverity?.level ?? -1;
  const depressionLevel = depressionSeverity?.level ?? -1;

  const anxietyElevated = anxietyScore != null && anxietyScore >= 22;
  const depressionElevated = depressionScore != null && depressionScore >= 14;

  if (!anxietyElevated && !depressionElevated) {
    return {
      type: "equilibrado",
      label: "Perfil bajo / equilibrado",
      description: "No predominan síntomas ansiosos ni depresivos elevados.",
    };
  }

  if (anxietyElevated && depressionElevated) {
    const levelGap = Math.abs(anxietyLevel - Math.max(depressionLevel - 1, 0));
    if (levelGap <= 1) {
      return {
        type: "mixto",
        label: "Perfil mixto",
        description:
          "Coexisten niveles relevantes de sintomatología ansiosa y depresiva.",
      };
    }
  }

  if (
    anxietyElevated &&
    (!depressionElevated || anxietyLevel > depressionLevel - 1)
  ) {
    return {
      type: "ansioso",
      label: "Perfil ansioso",
      description: "Predomina la sintomatología ansiosa.",
    };
  }

  return {
    type: "depresivo",
    label: "Perfil depresivo",
    description: "Predomina la sintomatología depresiva.",
  };
};

export const buildBeckAffectiveSpectrum = (tests = []) => {
  const anxietyTests = tests.filter((test) =>
    matchesTest(test.testName, ANXIETY_ALIASES)
  );
  const depressionTests = tests.filter((test) =>
    matchesTest(test.testName, DEPRESSION_ALIASES)
  );

  const latestAnxiety = pickLatestTest(anxietyTests);
  const latestDepression = pickLatestTest(depressionTests);

  const anxietyScore = getScore(latestAnxiety);
  const depressionScore = getScore(latestDepression);

  return {
    hasData: anxietyScore != null || depressionScore != null,
    anxiety: {
      score: anxietyScore,
      severity: getBeckSeverity(BECK_ANXIETY_RANGES, anxietyScore),
      testDay: latestAnxiety?.testDay ?? null,
    },
    depression: {
      score: depressionScore,
      severity: getBeckSeverity(BECK_DEPRESSION_RANGES, depressionScore),
      testDay: latestDepression?.testDay ?? null,
    },
    profile: getAffectiveProfile(anxietyScore, depressionScore),
  };
};
